import React from 'react';

interface AnimatedBorderPillProps {
  text: string;
  icon?: React.ElementType;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * A stable animated border pill badge that uses global CSS classes
 * to prevent animation freezing/crashing during theme toggles.
 * 
 * Uses CSS variables and global keyframes from index.css:
 * - badge-animated-border: rotating gradient animation
 * - badge-static-border: static border underneath
 * - badge-inner-bg: inner background color
 * - --badge-icon, --badge-text: text/icon colors
 */
const AnimatedBorderPill: React.FC<AnimatedBorderPillProps> = ({
  text,
  icon: Icon,
  className = '',
  size = 'md'
}) => {
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 gap-1' 
    : 'px-2.5 py-1 gap-1.5';
  
  const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs';

  return (
    <div className={`relative inline-flex items-center ${sizeClasses} rounded-full overflow-hidden ${className}`}>
      {/* Animated border - uses global CSS keyframes */}
      <div className="absolute inset-0 rounded-full badge-animated-border" />
      
      {/* Static border underneath */}
      <div className="absolute inset-0 rounded-full badge-static-border" />
      
      {/* Inner background */}
      <div className="absolute inset-[1px] rounded-full badge-inner-bg" />
      
      {/* Icon */}
      {Icon && (
        <Icon 
          className={`relative z-10 ${iconSize}`} 
          style={{ color: 'var(--badge-icon)' }} 
        />
      )}
      
      {/* Text */}
      <span 
        className={`relative z-10 ${textSize} font-medium`} 
        style={{ color: 'var(--badge-text)' }}
      >
        {text}
      </span>
    </div>
  );
};

export default AnimatedBorderPill;
